// The public team-registration page: /join/:code and /join.
//
// A golf course emails this link to its sponsor list months before the event. Whoever
// opens it is anonymous — no account, no sign-in, quite possibly standing in a car park on
// a phone. The page therefore does exactly three things in order: work out which event
// this is, show the captain enough of it that they can tell they are in the right place,
// and take the registration.
//
// The lookup before the form is not decoration. A captain who mistypes a code and only
// finds out after filling in four names has been made to do the work twice, and the codes
// are deliberately ambiguity-proof precisely so that never has to happen.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  normalizeJoinCode,
  isValidJoinCode,
  joinCodeProblem,
  formatJoinCode,
} from '@shared/sync/codes';
import { supabase, errText } from '../lib/supabase';
import SignupForm, { seatCount, type SignupResult, type Tournament } from '../components/SignupForm';

// Only the parts of the payload this page actually reads. `teams` carries entrants (each
// with a `withdrawn` flag) and the leaderboard cares about all of it; registration does
// not. A captain signing up a new team has no business reading the rest of the field's
// roster, so it is deliberately left untyped and unrendered here.
interface JoinPayload {
  tournament: Tournament;
  teams?: Array<{ id: string; name: string }> | null;
  contests?: Array<{ hole_idx: number; kind: string; label: string | null }> | null;
}

/**
 * `starts_on` is a DATE, not an instant. Handing "2026-08-15" to `new Date()` parses it as
 * UTC midnight and then prints it in the viewer's zone, which in Chicago shows the day
 * before. Build it explicitly and read it back in UTC so the date on the screen is the
 * date on the invitation.
 */
function formatEventDate(iso: string | null): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatLabel(format: string | null): string | null {
  if (!format) return null;
  if (format === 'scramble') return 'Scramble';
  if (format === 'individual') return 'Individual stroke play';
  return format;
}

/** A count from jsonb, or null if it is not a usable number. */
function num(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const shell: CSSProperties = { maxWidth: 620 };

export default function SignupPage() {
  const params = useParams<{ code?: string }>();
  const urlCode = params.code ?? '';

  const [raw, setRaw] = useState(urlCode);
  const [event, setEvent] = useState<JoinPayload | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [done, setDone] = useState<SignupResult | null>(null);
  // A code that arrived in the URL is normally settled, so the box gets out of the way once
  // the event resolves. It still has to be reachable: a course that reuses a mailing list
  // will send somebody last year's link, and "this is the wrong event" needs an answer
  // better than editing the address bar.
  const [codeOpen, setCodeOpen] = useState(false);

  // Which code we have already spent a request on. Keeps the effect below from firing in a
  // loop, and keeps a slow response for an old code from overwriting a newer one.
  const attempted = useRef<string | null>(null);

  const code = normalizeJoinCode(raw);
  const typingProblem = joinCodeProblem(raw);

  const lookup = useCallback(async (target: string) => {
    attempted.current = target;
    setLooking(true);
    setLookupError(null);
    try {
      const { data, error } = await supabase.rpc('join_tournament', { p_join_code: target });
      if (attempted.current !== target) return; // they typed on; this answer is stale
      if (error) {
        // "That code does not match an event" / "X has been cancelled" — the server's own
        // words, which say more than any wording this page could invent.
        setLookupError(errText(error));
        setEvent(null);
        return;
      }
      const payload = data as JoinPayload | null;
      if (!payload || !payload.tournament) {
        setLookupError('That code does not match an event');
        setEvent(null);
        return;
      }
      setEvent(payload);
    } catch (err) {
      if (attempted.current !== target) return;
      setLookupError(errText(err));
      setEvent(null);
    } finally {
      if (attempted.current === target) setLooking(false);
    }
  }, []);

  // Look the event up the moment a complete, plausible code exists — whether it arrived in
  // the URL or was typed. Six characters is short enough that waiting for a button press
  // just adds a tap.
  useEffect(() => {
    if (done) return;
    if (!isValidJoinCode(raw)) return;
    const target = normalizeJoinCode(raw);
    if (attempted.current === target) return;
    void lookup(target);
  }, [raw, done, lookup]);

  // Backing out of a finished code clears the event so the page does not show one thing
  // while the box says another.
  useEffect(() => {
    if (!isValidJoinCode(raw) && event) {
      setEvent(null);
      setLookupError(null);
      attempted.current = null;
    }
  }, [raw, event]);

  function retry(e: FormEvent) {
    e.preventDefault();
    if (!isValidJoinCode(raw)) return;
    attempted.current = null;
    void lookup(normalizeJoinCode(raw));
  }

  // ── Confirmation ──────────────────────────────────────────────────────────
  if (done && event) {
    const t = event.tournament;
    const when = formatEventDate(t.starts_on);
    return (
      <div className="wrap" style={shell}>
        <div className="card">
          <p
            style={{
              margin: 0,
              color: 'var(--ok)',
              fontWeight: 800,
              letterSpacing: '.6px',
              textTransform: 'uppercase',
              fontSize: 13,
            }}
          >
            You are registered
          </p>
          <h1 style={{ margin: '8px 0 4px', fontSize: 26 }}>{done.teamName}</h1>
          <p style={{ margin: 0, color: 'var(--ink-dim)' }}>
            is in the field for <strong style={{ color: 'var(--ink)' }}>{t.name}</strong>
            {when ? ` · ${when}` : ''}
            {t.course_name ? ` · ${t.course_name}` : ''}
          </p>

          <hr
            style={{
              border: 0,
              borderTop: '1px solid var(--paper-border)',
              margin: '18px 0 14px',
            }}
          />

          <p style={{ margin: '0 0 8px' }}>
            {done.namesGiven === 0
              ? `We have your foursome name and ${done.seats} open spots. No player names are needed yet.`
              : `We have ${done.namesGiven} of ${done.seats} player names.`}
          </p>
          <p style={{ margin: 0, color: 'var(--ink-dim)' }}>
            Names can be added or changed any time before you tee off — contact the event
            host and they will update your card.
          </p>
        </div>

        <div style={{ marginTop: 16 }}>
          <button
            className="secondary"
            onClick={() => setDone(null)}
            style={{ width: '100%' }}
          >
            Register another foursome
          </button>
        </div>
      </div>
    );
  }

  // ── Code entry + event summary + form ─────────────────────────────────────
  const t = event?.tournament;
  const when = t ? formatEventDate(t.starts_on) : null;
  const fmt = t ? formatLabel(t.format) : null;
  const contests = (event?.contests ?? []).filter(Boolean);
  const showCode = codeOpen || !urlCode || !!lookupError || !event;

  // How full the field is. `teams_taken` is the authority; the `teams` array is only a
  // fallback for an older RPC that does not send the count.
  const taken = num(t?.teams_taken) ?? event?.teams?.length ?? 0;
  const maxTeams = num(t?.max_teams);
  const remaining = maxTeams === null ? null : Math.max(maxTeams - taken, 0);

  // Belt and braces. The server enforces every one of these on submit and says so in its
  // own words; checking here only spares a captain filling in a form that cannot be
  // accepted. Someone can still take the last slot while they type, and that case lands on
  // the submit error — which is why the form keeps rendering the server's message verbatim
  // rather than trusting this snapshot.
  const closedMessage = !t
    ? null
    : t.status === 'final'
      ? `${t.name} is finished, so registration is closed.`
      : t.signups_open === false
        ? `Signups for ${t.name} are closed.`
        : remaining === 0
          ? `The field for ${t.name} is full.`
          : null;

  // Only worth saying when it is actually scarce. "3 of 40 teams registered" tells a
  // sponsor the event is empty, which is both discouraging and none of their business —
  // whereas "38 of 40" is a reason to finish the form now. Scale the threshold so a
  // 200-team field does not start nagging at 190.
  const nearlyFull =
    maxTeams !== null &&
    remaining !== null &&
    remaining > 0 &&
    remaining <= Math.max(4, Math.ceil(maxTeams * 0.1));

  return (
    <div className="wrap" style={shell}>
      <h1 style={{ fontSize: 28 }}>Foursome registration</h1>
      <p className="muted" style={{ margin: '0 0 18px' }}>
        One person signs up the whole foursome. It takes about a minute.
      </p>

      {showCode ? (
        <form className="card" onSubmit={retry}>
          <div className="field" style={{ marginBottom: event ? 0 : 12 }}>
            <label htmlFor="join-code">Event code</label>
            <input
              id="join-code"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              maxLength={12}
              autoCapitalize="characters"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              placeholder="6 characters"
              style={{
                textTransform: 'uppercase',
                letterSpacing: '3px',
                fontSize: 20,
                fontWeight: 700,
              }}
            />
            <p style={{ margin: '4px 0 0', color: 'var(--ink-dim)', fontSize: 13 }}>
              It is in the email from the course. Upper or lower case is fine.
            </p>
            {typingProblem ? (
              <p className="err" style={{ margin: '4px 0 0', fontSize: 13 }}>
                {typingProblem}
              </p>
            ) : null}
          </div>

          {looking ? (
            <p style={{ margin: 0, color: 'var(--ink-dim)' }}>
              Looking up {formatJoinCode(code)}…
            </p>
          ) : null}

          {lookupError && !looking ? (
            <>
              <p className="err" style={{ margin: '0 0 12px' }} role="alert">
                {lookupError}
              </p>
              <button type="submit" disabled={!isValidJoinCode(raw)}>
                Try again
              </button>
            </>
          ) : null}
        </form>
      ) : null}

      {t ? (
        <div className="card" style={{ marginTop: 16, background: 'var(--paper-alt)' }}>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: '.6px',
              color: 'var(--ink-dim)',
              fontWeight: 700,
            }}
          >
            You are signing up for
          </p>
          <h2 style={{ margin: '6px 0 6px', fontSize: 22 }}>{t.name}</h2>
          <p style={{ margin: 0, color: 'var(--ink-dim)' }}>
            {[when, t.course_name, fmt, `Foursomes of ${seatCount(t)}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {nearlyFull ? (
            <p style={{ margin: '6px 0 0', color: 'var(--ink)', fontSize: 13, fontWeight: 700 }}>
              {taken} of {maxTeams} foursomes registered — {remaining}{' '}
              {remaining === 1 ? 'spot' : 'spots'} left.
            </p>
          ) : taken > 0 ? (
            <p style={{ margin: '6px 0 0', color: 'var(--ink-dim)', fontSize: 13 }}>
              {taken} {taken === 1 ? 'foursome is' : 'foursomes are'} already in the field.
            </p>
          ) : null}
          {contests.length > 0 ? (
            <p style={{ margin: '6px 0 0', color: 'var(--ink-dim)', fontSize: 13 }}>
              Contests: {contests.map((c) => c.label || c.kind).join(', ')}
            </p>
          ) : null}
          {!showCode ? (
            <button
              type="button"
              onClick={() => setCodeOpen(true)}
              style={{
                marginTop: 10,
                padding: 0,
                background: 'none',
                border: 0,
                color: 'var(--ink-dim)',
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'underline',
              }}
            >
              Not the right event?
            </button>
          ) : null}
        </div>
      ) : null}

      {t && closedMessage ? (
        <div className="card" style={{ marginTop: 16 }} role="alert">
          <p className="err" style={{ margin: 0 }}>
            {closedMessage}
          </p>
          <p style={{ margin: '6px 0 0', color: 'var(--ink-dim)', fontSize: 13 }}>
            If you were expecting a spot, contact the event host — they can still make room
            from their end.
          </p>
        </div>
      ) : null}

      {t && !closedMessage ? (
        <SignupForm code={code} tournament={t} onRegistered={setDone} />
      ) : null}
    </div>
  );
}
